import React from 'react';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { RadioButton } from 'primereact/radiobutton';
import { Dropdown } from 'primereact/dropdown';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import LmsCodeDropdown from '../../../components/LmsCodeDropdown';
import useLmsLanguageOptions from '../../../hooks/useLmsLanguageOptions';

/**
 * Eğitim Kataloğu ekleme/düzenleme form alanları.
 * Bkz. docs/LMS-Egitim-Katalogu-ve-Egitmenler-API 2.md §3.1-3.3, §3.6.
 *
 * Eskiden `TrainingCatalogForm.jsx` içinde bir Dialog'du; artık sayfanın (`TrainingCatalogCreateEdit.jsx`)
 * üst kısmına gömülü, salt sunum katmanı bir form parçası — state ve kaydetme mantığı sayfada.
 *
 * 3 sütunlu grid (`md:col-4`), her input kendi grid hücresi içinde tam genişlik (`p-fluid`).
 * "Eğitim Kodu" alanı salt okunur bir önizleme: düzenlemede backend'den gelen gerçek `trainingCode`,
 * eklemede henüz kayıt yokken `codePreview` prop'u (üst sayfada iki dropdown'un KODUNDAN üretilir,
 * bkz. `TrainingCatalogCreateEdit.jsx`) — kayıttan önce hiçbir açıklama/placeholder metni yok, seçim
 * yapılmadıysa alan boş görünür.
 */
const TrainingCatalogFormFields = ({ formData, setFormData, isFieldInvalid, isEdit, codePreview }) => {
    const { th } = useLanguage();

    const { languageOptions, isLoadingLanguages } = useLmsLanguageOptions();

    return (
        <div className="grid p-fluid">
            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="vehicleModel">
                        {th('trainingCatalog.fields.vehicleModel')} <span className="text-red-500">*</span>
                    </label>
                    <LmsCodeDropdown
                        id="vehicleModel"
                        groupCode="VEHICLE_MODEL"
                        value={formData.vehicleModel}
                        onChange={(value) => setFormData((prev) => ({ ...prev, vehicleModel: value }))}
                        placeholder={th('trainingCatalog.fields.selectPlaceholder')}
                        invalid={isFieldInvalid('vehicleModel')}
                    />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="trainingType">
                        {th('trainingCatalog.fields.trainingType')} <span className="text-red-500">*</span>
                    </label>
                    <LmsCodeDropdown
                        id="trainingType"
                        groupCode="TRAINING_TYPES"
                        value={formData.trainingType}
                        onChange={(value) => setFormData((prev) => ({ ...prev, trainingType: value }))}
                        placeholder={th('trainingCatalog.fields.selectPlaceholder')}
                        invalid={isFieldInvalid('trainingType')}
                    />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label>{th('trainingCatalog.fields.trainingCode')}</label>
                    <InputText value={codePreview} disabled />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="trainingLanguage">
                        {th('trainingCatalog.fields.trainingLanguage')} <span className="text-red-500">*</span>
                    </label>
                    <Dropdown
                        id="trainingLanguage"
                        value={formData.trainingLanguage || null}
                        options={languageOptions}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(e) => setFormData((prev) => ({ ...prev, trainingLanguage: e.value }))}
                        placeholder={th('trainingCatalog.fields.selectPlaceholder')}
                        loading={isLoadingLanguages}
                        filter
                        showClear
                        className={isFieldInvalid('trainingLanguage') ? 'p-invalid' : ''}
                    />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="trainingLocation">
                        {th('trainingCatalog.fields.trainingLocation')} <span className="text-red-500">*</span>
                    </label>
                    <LmsCodeDropdown
                        id="trainingLocation"
                        groupCode="TRAINING_LOCATION"
                        value={formData.trainingLocation}
                        onChange={(value) => setFormData((prev) => ({ ...prev, trainingLocation: value }))}
                        placeholder={th('trainingCatalog.fields.selectPlaceholder')}
                        invalid={isFieldInvalid('trainingLocation')}
                    />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="grid">
                    <div className="col-6">
                        <div className="field">
                            <label htmlFor="trainingDuration">
                                {th('trainingCatalog.fields.trainingDuration')} <span className="text-red-500">*</span>
                            </label>
                            <InputNumber
                                id="trainingDuration"
                                value={formData.trainingDuration}
                                min={1}
                                onValueChange={(e) => setFormData((prev) => ({ ...prev, trainingDuration: e.value }))}
                                className={isFieldInvalid('trainingDuration') ? 'p-invalid' : ''}
                            />
                        </div>
                    </div>

                    <div className="col-6">
                        <div className="field">
                            <label htmlFor="trainingDay">
                                {th('trainingCatalog.fields.trainingDay')} <span className="text-red-500">*</span>
                            </label>
                            <InputText id="trainingDay" value={formData.trainingDay} maxLength={100} onChange={(e) => setFormData((prev) => ({ ...prev, trainingDay: e.target.value }))} className={isFieldInvalid('trainingDay') ? 'p-invalid' : ''} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="field">
                    <label htmlFor="trainingCapacity">
                        {th('trainingCatalog.fields.trainingCapacity')} <span className="text-red-500">*</span>
                    </label>
                    <InputNumber
                        id="trainingCapacity"
                        value={formData.trainingCapacity}
                        min={1}
                        onValueChange={(e) => setFormData((prev) => ({ ...prev, trainingCapacity: e.value }))}
                        className={isFieldInvalid('trainingCapacity') ? 'p-invalid' : ''}
                    />
                </div>
            </div>

            <div className="col-12 md:col-4">
                <div className="grid">
                    <div className="col-6">
                        <div className="field">
                            <label>{th('trainingCatalog.fields.preExam')}</label>
                            <div className="grid mt-2">
                                <div className="col-6 flex align-items-center">
                                    <RadioButton inputId="preExamYes" name="preExam" value={1} onChange={(e) => setFormData((prev) => ({ ...prev, preExam: e.value }))} checked={Number(formData.preExam) === 1} />
                                    <label htmlFor="preExamYes" className="ml-2 cursor-pointer">
                                        {th('common.yes', { fallback: 'Evet' })}
                                    </label>
                                </div>
                                <div className="col-6 flex align-items-center">
                                    <RadioButton inputId="preExamNo" name="preExam" value={0} onChange={(e) => setFormData((prev) => ({ ...prev, preExam: e.value }))} checked={Number(formData.preExam) === 0} />
                                    <label htmlFor="preExamNo" className="ml-2 cursor-pointer">
                                        {th('common.no', { fallback: 'Hayır' })}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-6">
                        <div className="field">
                            <label>{th('common.status')}</label>
                            <div className="grid mt-2">
                                <div className="col-6 flex align-items-center">
                                    <RadioButton inputId="isActiveYes" name="isActive" value={1} onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.value }))} checked={Number(formData.isActive) === 1} />
                                    <label htmlFor="isActiveYes" className="ml-2 cursor-pointer">
                                        {th('common.active', { fallback: 'Aktif' })}
                                    </label>
                                </div>
                                <div className="col-6 flex align-items-center">
                                    <RadioButton inputId="isActiveNo" name="isActive" value={0} onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.value }))} checked={Number(formData.isActive) === 0} />
                                    <label htmlFor="isActiveNo" className="ml-2 cursor-pointer">
                                        {th('common.inactive', { fallback: 'Pasif' })}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TrainingCatalogFormFields;
